import {
  ConditionFieldEnum,
  type BusinessSegmentConditions,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import { partnerApi, type ApiResponse } from '../../../lib/api';
import { API_ENDPOINTS } from '../../../lib/constants';
export interface CreateSegmentDefinitionResponse {
  id: string;
  canonicalVersionId: string;
}

export interface CreateSegmentDefinitionParams {
  name: string;
  description: string;
  contents?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
  targetedTenantsFilter?: string[] | null;
}

export interface GetSegmentDefinitionResponse {
  id: string;
  name: string;
  description: string | null;
  canonicalVersionId: string;
  contents: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
  updatedAt: string | null;
  targetedTenantsFilter: string[] | null;
  /** Business IDs that have this segment in an active automation; partner cannot deselect these tenants until automations are turned off. */
  businessIdsWithSegmentInActiveAutomation: string[];
  /** For each business ID, the names of automations that use this segment (for conflict messaging). */
  conflictingAutomationNamesByBusinessId: Record<string, string[]>;
}

/**
 * Legacy format = conditions (contents) exist AND condition_groups is empty or not provided.
 * Use this everywhere we need to decide between legacy (conditions) vs new (conditionGroups).
 */
export function isLegacySegmentFormat(args: {
  conditions?: BusinessSegmentConditions | null;
  conditionGroups?: SegmentConditionGroups | null;
}): boolean {
  return (
    args.conditions !== undefined &&
    args.conditions !== null &&
    (!args.conditionGroups || args.conditionGroups.length === 0)
  );
}

export interface UpdateSegmentDefinitionParams {
  name?: string;
  description?: string;
  contents?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups | null;
  targetedTenantsFilter?: string[] | null;
}

export interface UpdateSegmentDefinitionResponse {
  id: string;
}

export interface FieldToExpose {
  name: string;
  description?: string;
  jsonPath: string;
  exposure: string;
}

export interface SchemaDefinitionWithFields {
  id: string;
  name: string;
  plural_name?: string;
  pluralName?: string;
  category?: string;
  reference_schemas?: string[] | null;
  referenceSchemas?: string[] | null;
  schema?: Record<string, unknown>;
  fields_to_expose?: FieldToExpose[] | null;
  fieldsToExpose?: FieldToExpose[] | null;
}

/**
 * Field metadata for segment builder (type used for operator filtering).
 * Matches JSON Schema primitive types plus 'date' for date/date-time format.
 * When the schema property has an enum, enum values are included for dropdowns.
 * - name: value persisted to JSON (jsonPath-derived, e.g. "status"); used as condition.field.
 * - label: friendly name for dropdown display (e.g. from fields_to_expose.name); falls back to name.
 */
export interface FieldInfo {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'unknown';
  /** Friendly display name for dropdowns; when missing, use name. */
  label?: string;
  description?: string;
  /** Allowed values when the schema property defines an enum (for dropdowns). */
  enum?: string[];
}

/** Related resource schema for condition groups (cross-object references). */
export interface RelatedSchema {
  id: string;
  name: string;
  foreignKeyPath: string;
  fields: string[];
  /** Legacy (contents) format uses explicit names; for validation when useLegacyFieldNames. */
  fieldsLegacy?: string[];
}

/** Related schema with field metadata (types) for visual builder. */
export interface RelatedSchemaWithFieldTypes extends Omit<RelatedSchema, 'fields'> {
  fields: FieldInfo[];
}

/** Structured response for available segment fields including related resources. */
export interface AvailableFieldsResponse {
  contactFields: string[];
  /** Legacy (contents) format field names (fields_to_expose.name); use for validation when segment is legacy. */
  contactFieldsLegacy?: string[];
  relatedSchemas: RelatedSchema[];
}

/** Available fields response with types for visual segment builder. */
export interface AvailableFieldsResponseWithTypes {
  contactFields: FieldInfo[];
  relatedSchemas: RelatedSchemaWithFieldTypes[];
}

/**
 * Create a new segment definition.
 * platformId is automatically derived from auth context.
 */
export async function createSegmentDefinitionFromTemplate(
  params: CreateSegmentDefinitionParams,
): Promise<CreateSegmentDefinitionResponse> {
  const body: Record<string, unknown> = {
    name: params.name,
    description: params.description,
    contents: params.contents ?? [],
    conditionGroups: params.conditionGroups,
  };
  if (params.targetedTenantsFilter !== undefined) {
    body.targetedTenantsFilter = params.targetedTenantsFilter;
  }

  const response = await partnerApi.request<ApiResponse<CreateSegmentDefinitionResponse>>(
    API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to create segment');
  }

  return response.data;
}

/** Get a segment definition by canonical version ID. */
export async function getSegmentDefinition(
  canonicalVersionId: string,
): Promise<GetSegmentDefinitionResponse> {
  const response = await partnerApi.request<ApiResponse<GetSegmentDefinitionResponse>>(
    `${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`,
  );

  return response.data;
}

/** Update a segment definition. */
export async function updateSegmentDefinition(
  canonicalVersionId: string,
  params: UpdateSegmentDefinitionParams,
): Promise<UpdateSegmentDefinitionResponse> {
  const response = await partnerApi.request<ApiResponse<UpdateSegmentDefinitionResponse>>(
    `${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to update segment');
  }

  return response.data;
}

/**
 * Default built-in fields that are always available for segment conditions
 * Derived from ConditionFieldEnum to stay in sync with backend
 */
const DEFAULT_FIELDS = ConditionFieldEnum.options;

const CONTACTS_SCHEMA_CATEGORY = 'contacts_schema';

/**
 * Find the foreign key path (property name) that references the contact schema.
 * Checks for $ref matching by ID, plural name, or singular name since $ref uses
 * the schema's plural_name (e.g., "reach:schemas/Customers").
 */
function findForeignKeyPathToContact(
  schemaDef: Record<string, unknown> | undefined,
  contactSchemaId: string,
  contactPluralName: string | null,
  contactName: string | null,
): string | null {
  if (!schemaDef || typeof schemaDef !== 'object') return null;
  const properties = schemaDef.properties as Record<string, unknown> | undefined;
  if (!properties || typeof properties !== 'object') return null;

  // Build possible $ref patterns to match
  const possibleRefs = new Set<string>();
  possibleRefs.add(`reach:schemas/${contactSchemaId}`);
  if (contactPluralName) possibleRefs.add(`reach:schemas/${contactPluralName}`);
  if (contactName) possibleRefs.add(`reach:schemas/${contactName}`);

  for (const [key, prop] of Object.entries(properties)) {
    const p = prop as Record<string, unknown> | undefined;
    if (p && typeof p === 'object' && typeof p.$ref === 'string') {
      if (possibleRefs.has(p.$ref)) return key;
    }
  }
  return null;
}

/**
 * Extract field identifier from jsonPath (e.g., ".status" → "status").
 */
function fieldNameFromJsonPath(jsonPath: string): string | null {
  if (!jsonPath || typeof jsonPath !== 'string') return null;
  // Handle ".fieldName" format
  if (jsonPath.startsWith('.')) return jsonPath.slice(1) || null;
  return jsonPath;
}

/** Infer segment builder field type from JSON Schema property. */
function fieldTypeFromSchemaProperty(prop: Record<string, unknown> | undefined): FieldInfo['type'] {
  if (!prop || typeof prop !== 'object') return 'unknown';
  const type = prop.type as string | undefined;
  const format = prop.format as string | undefined;
  if (format === 'date' || format === 'date-time') return 'date';
  if (type === 'string') return 'string';
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'array') return 'array';
  return 'unknown';
}

/** Extract enum values from JSON Schema property; returns strings for dropdown use. */
function enumFromSchemaProperty(prop: Record<string, unknown> | undefined): string[] | undefined {
  if (!prop || typeof prop !== 'object') return undefined;
  const enumVal = prop.enum;
  if (!Array.isArray(enumVal) || enumVal.length === 0) return undefined;
  return enumVal.map(v => (v === null || v === undefined ? '' : String(v)));
}

/**
 * Get property at jsonPath from schema.properties (supports top-level only, e.g. ".status").
 */
function getSchemaProperty(
  schema: SchemaDefinitionWithFields,
  jsonPath: string,
): Record<string, unknown> | undefined {
  const schemaObj = schema.schema;
  if (!schemaObj || typeof schemaObj !== 'object') return undefined;
  const props = schemaObj.properties as Record<string, unknown> | undefined;
  if (!props || typeof props !== 'object') return undefined;
  const fieldName = fieldNameFromJsonPath(jsonPath);
  if (!fieldName) return undefined;
  return props[fieldName] as Record<string, unknown> | undefined;
}

/**
 * Get field names from schema's fields_to_expose (using jsonPath).
 * Returns empty array if fields_to_expose is empty/null (no fallback).
 */
function getSchemaFieldNames(schema: SchemaDefinitionWithFields): string[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const fields: string[] = [];
  for (const f of expose) {
    const fieldName = fieldNameFromJsonPath(f.jsonPath);
    if (fieldName) fields.push(fieldName);
  }
  return fields;
}

/**
 * Get field names from schema's fields_to_expose (explicit name field).
 * For use with legacy format (contents) only. Returns empty array if empty/null.
 */
function getSchemaFieldNamesLegacy(schema: SchemaDefinitionWithFields): string[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const fields: string[] = [];
  for (const f of expose) {
    if (f.name && typeof f.name === 'string') fields.push(f.name);
  }
  return fields;
}

/**
 * Get field metadata (name, type, label, description) from schema's fields_to_expose.
 * - name: jsonPath-derived value persisted to JSON (condition.field); e.g. ".status" → "status".
 * - label: friendly name for dropdown (fields_to_expose.name when present); falls back to name.
 */
function getSchemaFieldInfos(schema: SchemaDefinitionWithFields): FieldInfo[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const infos: FieldInfo[] = [];
  for (const f of expose) {
    const pathName = fieldNameFromJsonPath(f.jsonPath);
    if (!pathName) continue;
    const prop = getSchemaProperty(schema, f.jsonPath);
    const type = fieldTypeFromSchemaProperty(prop);
    const enumValues = enumFromSchemaProperty(prop);
    const label = f.name && typeof f.name === 'string' ? f.name : pathName;
    infos.push({
      name: pathName,
      type,
      label,
      description: typeof f.description === 'string' ? f.description : undefined,
      ...(enumValues && enumValues.length > 0 && { enum: enumValues }),
    });
  }
  return infos.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Fetch available fields from schema definitions.
 * Returns contact fields and related resource schemas that reference the contact schema
 * (for condition groups / cross-object segment conditions).
 */
export async function fetchAvailableFields(): Promise<AvailableFieldsResponse> {
  const response = await partnerApi.request<ApiResponse<SchemaDefinitionWithFields[]>>(
    API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS,
  );

  const schemas = response.data;
  const contactSchema = schemas.find(s => (s.category ?? '') === CONTACTS_SCHEMA_CATEGORY);
  const contactSchemaId = contactSchema?.id ?? null;

  const contactFields: string[] = [...DEFAULT_FIELDS];
  const contactFieldsLegacy: string[] = [...DEFAULT_FIELDS];
  if (contactSchema) {
    for (const name of getSchemaFieldNames(contactSchema)) {
      if (!contactFields.includes(name)) contactFields.push(name);
    }
    for (const name of getSchemaFieldNamesLegacy(contactSchema)) {
      if (!contactFieldsLegacy.includes(name)) contactFieldsLegacy.push(name);
    }
  }
  contactFields.sort();
  contactFieldsLegacy.sort();

  const relatedSchemas: RelatedSchema[] = [];
  if (contactSchemaId && contactSchema) {
    const contactPluralName = contactSchema.plural_name ?? contactSchema.pluralName ?? null;
    const contactName = contactSchema.name ?? null;
    const refs = (s: SchemaDefinitionWithFields) => s.reference_schemas ?? s.referenceSchemas ?? [];
    for (const schema of schemas) {
      if (schema.id === contactSchemaId) continue;
      if (!refs(schema)?.includes(contactSchemaId)) continue;
      const schemaObj = schema.schema;
      const foreignKeyPath = findForeignKeyPathToContact(
        schemaObj,
        contactSchemaId,
        contactPluralName,
        contactName,
      );
      if (!foreignKeyPath) continue;
      const fields = getSchemaFieldNames(schema);
      const fieldsLegacy = getSchemaFieldNamesLegacy(schema);
      relatedSchemas.push({
        id: schema.id,
        name: schema.name,
        foreignKeyPath,
        fields: [...fields].sort(),
        fieldsLegacy: fieldsLegacy.length > 0 ? [...fieldsLegacy].sort() : undefined,
      });
    }
  }

  return { contactFields, contactFieldsLegacy, relatedSchemas };
}

/**
 * Fetch available fields with types for the visual segment builder.
 * Returns contact fields and related schemas with FieldInfo (name, type, description).
 */
export async function fetchAvailableFieldsWithTypes(): Promise<AvailableFieldsResponseWithTypes> {
  const response = await partnerApi.request<ApiResponse<SchemaDefinitionWithFields[]>>(
    API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS,
  );

  const schemas = response.data;
  const contactSchema = schemas.find(s => (s.category ?? '') === CONTACTS_SCHEMA_CATEGORY);
  const contactSchemaId = contactSchema?.id ?? null;

  const contactFields: FieldInfo[] = DEFAULT_FIELDS.map(name => ({
    name,
    type: 'string',
  }));
  if (contactSchema) {
    const infos = getSchemaFieldInfos(contactSchema);
    for (const info of infos) {
      if (!contactFields.some(f => f.name === info.name)) {
        contactFields.push(info);
      }
    }
  }
  contactFields.sort((a, b) => a.name.localeCompare(b.name));

  const relatedSchemas: RelatedSchemaWithFieldTypes[] = [];
  if (contactSchemaId && contactSchema) {
    const contactPluralName = contactSchema.plural_name ?? contactSchema.pluralName ?? null;
    const contactName = contactSchema.name ?? null;
    const refs = (s: SchemaDefinitionWithFields) => s.reference_schemas ?? s.referenceSchemas ?? [];
    for (const schema of schemas) {
      if (schema.id === contactSchemaId) continue;
      if (!refs(schema)?.includes(contactSchemaId)) continue;
      const schemaObj = schema.schema;
      const foreignKeyPath = findForeignKeyPathToContact(
        schemaObj,
        contactSchemaId,
        contactPluralName,
        contactName,
      );
      if (!foreignKeyPath) continue;
      const fields = getSchemaFieldInfos(schema);
      relatedSchemas.push({
        id: schema.id,
        name: schema.name,
        foreignKeyPath,
        fields,
      });
    }
  }

  return { contactFields, relatedSchemas };
}

export interface PreviewRecipient {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface PreviewSegmentRecipientsResponse {
  recipients: PreviewRecipient[];
  pagination: {
    total: number;
    hasNextPage: boolean;
    cursor?: string;
  };
  estimatedTotal: number;
}

/**
 * Preview segment recipients for a given tenant
 * Uses the tenant-scoped endpoint with reach-tenant-id header.
 * Supports both legacy (conditions) and new (conditionGroups) formats.
 */
export async function previewSegmentRecipients(
  tenantId: string,
  conditions?: BusinessSegmentConditions,
  conditionGroups?: SegmentConditionGroups,
  limit = 20,
): Promise<PreviewSegmentRecipientsResponse> {
  const body: Record<string, unknown> = {
    limit,
  };

  if (isLegacySegmentFormat({ conditions, conditionGroups })) {
    body.conditions = conditions;
  } else if (conditionGroups !== undefined && conditionGroups !== null && conditionGroups.length > 0) {
    body.conditionGroups = conditionGroups;
  }

  const response = await partnerApi.request<ApiResponse<PreviewSegmentRecipientsResponse>>(
    API_ENDPOINTS.AUTOMATIONS.PREVIEW_SEGMENT_RECIPIENTS,
    {
      method: 'POST',
      headers: {
        'reach-tenant-id': tenantId,
      },
      body: JSON.stringify(body),
    },
  );

  return response.data;
}
